import { useQuery } from "@tanstack/react-query";
import { parseISO, subDays } from "date-fns";
import type React from "react";

import type { ResidentEngagementDto, ResidentTimeEngagementDto } from "@/api/types";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { DonutChart } from "@/components/Charts/DonutChart";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";

const CHART_SIZE = 160;
const FALLBACK_PRIMARY_DATA: ResidentTimeEngagementDto = {
  benchmarkEngagementPercentage: 0,
  engagementPercentage: 0,
  engagementCount: 0,
  from: subDays(new Date(), 7).toISOString(),
  to: new Date().toISOString(),
  type: "last7Days",
};
const FALLBACK_BENCHMARK_PERCENTAGE = 0;
const FALLBACK_PERIOD: ResidentEngagementDto["period"] = "zeroToOneMonth";

export function EngagementWidget(): React.ReactNode {
  const { t, i18n } = useTranslation();
  const slug = useSlug();

  const analyticsQueries = useAnalyticsQueries();
  const {
    data: engagementsData,
    isPending,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery(analyticsQueries.engagement());

  const primaryData =
    engagementsData?.engagements?.find((engagement) => engagement.type === "lastMonth") || FALLBACK_PRIMARY_DATA;
  const benchmarkPercentage = primaryData.benchmarkEngagementPercentage || FALLBACK_BENCHMARK_PERCENTAGE;
  const projectPeriod = engagementsData?.period || FALLBACK_PERIOD;
  const isDataAvailable = Boolean(primaryData.engagementPercentage);

  return (
    <AnalyticsWidgetCard.Root
      title={t("component.analytics-widget.engagement.title")}
      href={isDataAvailable ? routes.analytics.engagementDetails({ slug }) : undefined}
      isError={isError}
      isLoading={isPending || isRefetching}
      isDataAvailable={isDataAvailable}
      error={error}
      onReload={refetch}
    >
      <AnalyticsWidgetCard.Body>
        <DonutChart size={CHART_SIZE} value={primaryData?.engagementPercentage || 0} />
        <ChartLegend
          items={[
            {
              label: t("component.analytics-widget.engagement.chart.legend.primary"),
              color: dataColors.primary,
              tooltip: t("component.analytics-widget.engagement.chart.legend.primary.tooltip", {
                startDate: formatDate(i18n, "dateMonthYearShort", parseISO(primaryData.from)),
                endDate: formatDate(i18n, "dateMonthYearShort", parseISO(primaryData.to)),
              }),
            },
          ]}
        />
      </AnalyticsWidgetCard.Body>
      <AnalyticsWidgetCard.Footer>
        <AnalyticsWidgetCard.ReferenceLabel
          currValue={primaryData?.engagementPercentage || 0}
          referenceValue={benchmarkPercentage}
        />
        <AnalyticsWidgetCard.BenchmarkInfo benchmark={benchmarkPercentage} period={projectPeriod} />
      </AnalyticsWidgetCard.Footer>
    </AnalyticsWidgetCard.Root>
  );
}
